<template>
    <div class='settings-page'>
        <header class='page-header'>
            <div>
                <p class='page-kicker'>EVENT SETTINGS</p>
                <h1 class='title is-3'>詳細設定</h1>
                <p class='page-description'>
                    <strong v-if='selectEvent.name'>{{ selectEvent.name }}</strong>
                    <span v-if='selectEvent.name'> の</span>販売条件と価格を設定します。
                </p>
            </div>
            <div class='setting-header-buttons'>
                <button type='button' class='button is-light' @click='exportCsv'>
                    <span class='icon'><i class='fas fa-download' aria-hidden='true'></i></span>
                    <span>CSVエクスポート</span>
                </button>
                <button type='button' class='button is-link is-outlined' @click='$refs.csvInput.click()'>
                    <span class='icon'><i class='fas fa-upload' aria-hidden='true'></i></span>
                    <span>CSVインポート</span>
                </button>
                <input type='file' ref='csvInput' accept='.csv' style='display:none' @change='importCsv'>
            </div>
        </header>

        <section class='setting-card'>
            <div class='section-heading'>
                <div class='section-icon access-icon'>
                    <i class='fas fa-key' aria-hidden='true'></i>
                </div>
                <div>
                    <h2>閲覧用ID・パスワード</h2>
                    <p>保護者がこの行事の写真を閲覧するときに使用します。</p>
                </div>
            </div>
            <div class='form-grid access-grid'>
                <div class='field'>
                    <label class='label' for='event-browsing-id'>閲覧用ID</label>
                    <div class='control'>
                        <input id='event-browsing-id' class='input' type='text' placeholder='閲覧用IDを入力' v-model='selectEvent.browsing_id'>
                    </div>
                </div>
                <div class='field'>
                    <label class='label' for='event-password'>パスワード</label>
                    <div class='control'>
                        <input id='event-password' class='input' type='text' placeholder='パスワードを入力' v-model='selectEvent.password'>
                    </div>
                </div>
            </div>
        </section>

        <section class='setting-card'>
            <div class='section-heading'>
                <div class='section-icon period-icon'>
                    <i class='fas fa-calendar-alt' aria-hidden='true'></i>
                </div>
                <div>
                    <h2>販売期間</h2>
                    <p>設定した期間中、保護者が写真を閲覧・購入できます。</p>
                </div>
            </div>
            <div class='period-body'>
                <div class='date-range'>
                    <div class='field'>
                        <label class='label' for='sales-start-date'>販売開始日</label>
                        <input id='sales-start-date' class='input' type='date' v-model='selectEvent.salesStartDate'>
                    </div>
                    <span class='date-separator'>〜</span>
                    <div class='field'>
                        <label class='label' for='sales-end-date'>販売終了日</label>
                        <input id='sales-end-date' class='input' type='date' v-model='selectEvent.salesEndDate'>
                    </div>
                </div>
                <label class='outside-period-option'>
                    <input type='checkbox' v-model='selectEvent.salesOutsidePeriod'>
                    <span>
                        <strong>期間外販売を許可する</strong>
                        <small>販売終了後も、期間外手数料を加えて購入できるようにします。</small>
                    </span>
                </label>
            </div>
        </section>

        <section class='setting-card'>
            <div class='section-heading'>
                <div class='section-icon payment-icon'>
                    <i class='fas fa-credit-card' aria-hidden='true'></i>
                </div>
                <div>
                    <h2>決済方法</h2>
                    <p>この行事で利用できる決済方法と手数料を設定します。</p>
                </div>
            </div>
            <div class='setting-table'>
                <div class='setting-table-header'>
                    <span>決済方法</span>
                    <span>手数料</span>
                </div>
                <div v-for='(payment, i) in selectEvent.setting.payments' :key='i' class='setting-row'>
                    <label class='item-toggle'>
                        <input type='checkbox' v-model='selectEvent.setting.payments[i].use'>
                        <span>{{ payment.name }}</span>
                    </label>
                    <div class='price-control'>
                        <template v-if='payment.name === paymentMethod.CREDIT_CARD'>
                            <input type='text' class='input price-input' v-model='selectEvent.cardFee' disabled>
                            <span class='unit'>%</span>
                        </template>
                        <template v-else-if='payment.name === paymentMethod.CONVENIENCE_STORE'>
                            <input type='text' class='input price-input' v-model='selectEvent.conveniFee' disabled>
                            <span class='unit'>円</span>
                        </template>
                        <span v-else-if='isCustomerBornePaymentFee(payment)' class='fee-note'>{{ customerBornePaymentFeeText }}</span>
                        <template v-else>
                            <input type='number' class='input price-input' min='0' v-model='selectEvent.setting.payments[i].price'>
                            <span class='unit'>円</span>
                        </template>
                    </div>
                </div>
            </div>
        </section>

        <section class='setting-card'>
            <div class='section-heading'>
                <div class='section-icon delivery-icon'>
                    <i class='fas fa-truck' aria-hidden='true'></i>
                </div>
                <div>
                    <h2>配送方法</h2>
                    <p>この行事で選択できる配送方法と送料を設定します。</p>
                </div>
            </div>
            <div class='setting-table'>
                <div class='setting-table-header'>
                    <span>配送方法</span>
                    <span>送料</span>
                </div>
                <div v-for='(delivery, i) in selectEvent.setting.deliverys' :key='i' class='setting-row'>
                    <label class='item-toggle'>
                        <input type='checkbox' v-model='selectEvent.setting.deliverys[i].use'>
                        <span>{{ delivery.name }}</span>
                    </label>
                    <div class='price-control'>
                        <input
                            type='number'
                            class='input price-input'
                            v-model='selectEvent.setting.deliverys[i].price'
                            :min='delivery.name === deliveryMethod.YUU_PACKET ? selectEvent.yuuPacketFee : 0'
                        >
                        <span class='unit'>円</span>
                    </div>
                </div>
            </div>
            <p v-if='Number(selectEvent.yuuPacketFee)' class='section-note'>
                <i class='fas fa-info-circle' aria-hidden='true'></i>
                ゆうパケット便は {{ selectEvent.yuuPacketFee }}円以上で設定できます。
            </p>
        </section>

        <section class='setting-card'>
            <div class='section-heading'>
                <div class='section-icon size-icon'>
                    <i class='fas fa-image' aria-hidden='true'></i>
                </div>
                <div>
                    <h2>写真サイズと価格</h2>
                    <p>この行事で販売する写真サイズと価格を設定します。</p>
                </div>
            </div>
            <div class='setting-table'>
                <div class='setting-table-header'>
                    <span>写真サイズ</span>
                    <span>販売価格</span>
                </div>
                <div v-for='(size, i) in selectEvent.setting.sizes' :key='i' class='setting-row'>
                    <label class='item-toggle'>
                        <input type='checkbox' v-model='selectEvent.setting.sizes[i].use'>
                        <span>{{ size.name }}</span>
                    </label>
                    <div class='price-control'>
                        <input type='number' class='input price-input' min='0' v-model='selectEvent.setting.sizes[i].price'>
                        <span class='unit'>円</span>
                    </div>
                </div>
            </div>
            <div class='default-setting'>
                <div>
                    <label for='event-default-size'>購入時のデフォルトサイズ</label>
                    <p>購入画面を開いたときに最初に選択されるサイズです。</p>
                </div>
                <div class='select'>
                    <select id='event-default-size' v-model='selectEvent.setting.defaultSize'>
                        <option v-for='(size, i) in selectEvent.setting.sizes' :key='i' :value='size.name'>
                            {{ size.name }}
                        </option>
                    </select>
                </div>
            </div>
        </section>

        <div class='save-bar'>
            <p>変更内容はこの行事にのみ反映されます。</p>
            <button class='button is-info is-medium' @click='save'>
                <span class='icon'><i class='fas fa-save' aria-hidden='true'></i></span>
                <span>詳細設定を保存</span>
            </button>
        </div>

        <div class="modal" :class="{ 'is-active': importModalActive }">
            <div class="modal-background" @click='closeImportModal'></div>
            <div class="modal-card">
                <header class="modal-card-head">
                    <p class="modal-card-title">CSVインポート先を選択</p>
                    <button class="delete" aria-label="close" @click='closeImportModal'></button>
                </header>
                <section class="modal-card-body">
                    <p class='import-description'>CSVから反映する項目と適用範囲を選択してください。</p>
                    <p class='import-field-title'>適用する項目</p>
                    <div class='import-fields'>
                        <label class='import-option'>
                            <input type='checkbox' value='access' v-model='importApplyFields'>
                            <span>ID・パスワード</span>
                        </label>
                        <label class='import-option'>
                            <input type='checkbox' value='period' v-model='importApplyFields'>
                            <span>販売期間</span>
                        </label>
                        <label class='import-option'>
                            <input type='checkbox' value='display' v-model='importApplyFields'>
                            <span>表示・補正設定</span>
                        </label>
                        <label class='import-option'>
                            <input type='checkbox' value='availability' v-model='importApplyFields'>
                            <span>決済・配送・サイズの利用可否</span>
                        </label>
                        <label class='import-option'>
                            <input type='checkbox' value='prices' v-model='importApplyFields'>
                            <span>料金・手数料</span>
                        </label>
                        <label class='import-option'>
                            <input type='checkbox' value='defaultSize' v-model='importApplyFields'>
                            <span>デフォルトサイズ</span>
                        </label>
                    </div>
                    <div class='import-field-actions'>
                        <button class='button is-small is-light' type='button' @click='selectAllImportFields'>すべて選択</button>
                        <button class='button is-small is-light' type='button' @click='importApplyFields = []'>すべて解除</button>
                    </div>
                </section>
                <footer class="modal-card-foot import-modal-foot">
                    <button class="button is-light" @click='closeImportModal'>キャンセル</button>
                    <button class="button is-info" :disabled='!importApplyFields.length' @click='executeImport("event")'>現在の行事に適用</button>
                    <button class="button is-success" :disabled='!importApplyFields.length' @click='executeImport("year")'>年度全体に適用</button>
                </footer>
            </div>
        </div>
    </div>
</template>

<script>
import fileDownload from 'js-file-download'
import { CUSTOMER_BORNE_PAYMENT_FEE_TEXT, isCustomerBornePaymentFee } from '../../../helpers/payment'
import { PAYMENT_METHOD, DELIVERY_METHOD } from '../../../constants/orderOption'

export default {
    name: 'Setting',
    props: ['selectEvent'],
    data () {
        return {
            importModalActive: false,
            importFile: null,
            importApplyFields: ['access', 'period', 'display', 'availability', 'prices', 'defaultSize'],
            paymentMethod: PAYMENT_METHOD,
            deliveryMethod: DELIVERY_METHOD,
            customerBornePaymentFeeText: CUSTOMER_BORNE_PAYMENT_FEE_TEXT
        }
    },
    watch: {
        selectEvent: {
            immediate: true,
            handler (event) {
                this.initializeEventFields(event)
            }
        }
    },
    methods: {
        isCustomerBornePaymentFee,
        formatDate (value) {
            if (!value) return ''

            const date = new Date(value)
            if (Number.isNaN(date.getTime())) return ''

            const year = date.getFullYear()
            const month = ('00' + (date.getMonth() + 1)).slice(-2)
            const day = ('00' + date.getDate()).slice(-2)
            return `${year}-${month}-${day}`
        },
        initializeEventFields (event) {
            if (!event) return

            const salesOutsidePeriod = event.sales_outside_period === true ||
                event.sales_outside_period === 1 ||
                event.sales_outside_period === '1'
            this.$set(event, 'salesOutsidePeriod', salesOutsidePeriod)
            this.$set(event, 'salesStartDate', this.formatDate(event.sales_start_date))
            this.$set(event, 'salesEndDate', this.formatDate(event.sales_end_date))
        },
        save () {
            const self = this
            const eventId = this.selectEvent.id
            const params = this.$parent.$parent.requestParams
            const deliverys = self.selectEvent.setting.deliverys || []
            const yuuPacketDelivery = deliverys.find((delivery) => {
                return delivery.name === DELIVERY_METHOD.YUU_PACKET
            })
            const minYuuPacketFee = Number(self.selectEvent.yuuPacketFee)

            if (yuuPacketDelivery && yuuPacketDelivery.use !== false && Number(yuuPacketDelivery.price) < minYuuPacketFee) {
                alert(`ゆうパケット便送料は${minYuuPacketFee}円未満には設定できません`)
                return
            }

            self.axios.post(`${process.env.API_URL}/photographer/event/setting${params}`, {
                eventId: eventId,
                browsing_id: self.selectEvent.browsing_id,
                password: self.selectEvent.password,
                sales_outside_period: self.selectEvent.salesOutsidePeriod,
                sales_start_date: self.selectEvent.salesStartDate,
                sales_end_date: self.selectEvent.salesEndDate,
                setting: self.selectEvent.setting
            }).then((res) => {
                if (res.data.message === 'OK') {
                    alert('保存が完了しました')
                } else if (res.data.message === 'Duplicate') {
                    alert('このIDとパスワードの組み合わせは現在利用できません')
                } else if (res.data.message === 'YuuPacketFeeTooLow') {
                    alert(`ゆうパケット便送料は${res.data.yuuPacketFee}円未満には設定できません`)
                } else {
                    throw new Error()
                }
            }).catch((e) => {
                console.log(e)
                alert('サーバーに問題が発生しています')
            })
        },
        exportCsv () {
            const params = this.$parent.$parent.requestParams
            const sep = params ? '&' : '?'
            this.axios.get(`${process.env.API_URL}/photographer/event/csv/export${params}${sep}event_id=${this.selectEvent.id}`, {
                responseType: 'blob'
            }).then((res) => {
                fileDownload(res.data, 'event.csv')
            }).catch(() => {
                alert('CSVエクスポートに失敗しました')
            })
        },
        importCsv (e) {
            const file = e.target.files[0]
            if (!file) return
            this.importFile = file
            this.selectAllImportFields()
            this.importModalActive = true
            this.$refs.csvInput.value = ''
        },
        selectAllImportFields () {
            this.importApplyFields = ['access', 'period', 'display', 'availability', 'prices', 'defaultSize']
        },
        closeImportModal () {
            this.importModalActive = false
            this.importFile = null
        },
        executeImport (mode) {
            if (!this.importApplyFields.length) {
                alert('適用する項目を選択してください')
                return
            }
            this.importModalActive = false
            const params = this.$parent.$parent.requestParams
            const formData = new FormData()
            formData.append('csv', this.importFile)
            formData.append('apply_fields', JSON.stringify(this.importApplyFields))
            if (mode === 'event') {
                formData.append('event_id', this.selectEvent.id)
            } else {
                formData.append('year_id', this.$parent.$parent.selectYearId)
            }
            this.importFile = null
            this.axios.post(`${process.env.API_URL}/photographer/event/csv/import${params}`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            }).then((res) => {
                const data = res.data
                let msg = `インポート完了: 更新 ${data.updated}件`
                if (data.errors && data.errors.length) {
                    msg += `\nエラー ${data.errors.length}件:`
                    data.errors.forEach(err => { msg += `\n  行${err.row}: ${err.message}` })
                }
                alert(msg)
                location.reload()
            }).catch(() => {
                alert('CSVインポートに失敗しました')
            })
        }
    }
}
</script>

<style scoped>

.settings-page {
    width: 100%;
    max-width: 920px;
    padding-bottom: 44px;
}

.page-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 24px;
    margin-bottom: 24px;
}

.page-header .title {
    margin: 2px 0 8px;
    color: #1f2937;
}

.page-kicker {
    color: #3273dc;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.14em;
}

.page-description {
    color: #667085;
    font-size: 14px;
}

.page-description strong {
    color: #344054;
}

.setting-header-buttons {
    display: flex;
    flex: 0 0 auto;
    gap: 8px;
}

.setting-card {
    overflow: hidden;
    margin-bottom: 20px;
    border: 1px solid #e4e9f1;
    border-radius: 10px;
    background: #fff;
    box-shadow: 0 2px 8px rgba(31, 41, 55, 0.04);
}

.section-heading {
    display: flex;
    gap: 14px;
    align-items: center;
    padding: 22px 24px 18px;
}

.section-heading h2 {
    margin: 0 0 3px;
    color: #25324b;
    font-size: 17px;
    font-weight: 700;
}

.section-heading p {
    margin: 0;
    color: #7a8496;
    font-size: 13px;
}

.section-icon {
    display: flex;
    flex: 0 0 38px;
    align-items: center;
    justify-content: center;
    width: 38px;
    height: 38px;
    border-radius: 9px;
}

.access-icon {
    color: #7955b7;
    background: #f3effc;
}

.period-icon {
    color: #c35b31;
    background: #fff0ea;
}

.payment-icon {
    color: #3273dc;
    background: #edf4ff;
}

.delivery-icon {
    color: #16836b;
    background: #eaf8f4;
}

.size-icon {
    color: #9a6700;
    background: #fff6dd;
}

.form-grid {
    display: grid;
    gap: 20px;
    padding: 4px 24px 24px;
}

.access-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
}

.form-grid .field {
    margin-bottom: 0;
}

.form-grid .label,
.date-range .label {
    margin-bottom: 7px;
    color: #475467;
    font-size: 12px;
}

.period-body {
    padding: 4px 24px 24px;
}

.date-range {
    display: flex;
    align-items: flex-end;
    gap: 14px;
}

.date-range .field {
    flex: 1;
    margin-bottom: 0;
}

.date-separator {
    padding-bottom: 9px;
    color: #98a2b3;
}

.outside-period-option {
    display: flex;
    align-items: flex-start;
    gap: 11px;
    margin-top: 18px;
    padding: 14px 16px;
    border: 1px solid #e4e9f1;
    border-radius: 8px;
    background: #fafbfc;
    cursor: pointer;
}

.outside-period-option input {
    flex: 0 0 auto;
    width: 17px;
    height: 17px;
    margin-top: 2px;
    accent-color: #3273dc;
}

.outside-period-option span {
    display: flex;
    flex-direction: column;
    gap: 2px;
}

.outside-period-option strong {
    color: #344054;
    font-size: 13px;
}

.outside-period-option small {
    color: #7a8496;
    font-size: 12px;
}

.setting-table {
    border-top: 1px solid #edf0f5;
}

.setting-table-header,
.setting-row {
    display: grid;
    grid-template-columns: minmax(220px, 1fr) minmax(180px, 260px);
    align-items: center;
    padding: 0 24px;
}

.setting-table-header {
    min-height: 38px;
    color: #8992a3;
    background: #fafbfc;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.04em;
}

.setting-row {
    min-height: 58px;
    border-top: 1px solid #f0f2f5;
}

.item-toggle {
    display: inline-flex;
    align-items: center;
    gap: 10px;
    color: #344054;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
}

.item-toggle input {
    width: 17px;
    height: 17px;
    accent-color: #3273dc;
}

.price-control {
    display: flex;
    align-items: center;
    gap: 9px;
}

.price-input {
    width: 120px;
    height: 36px;
    text-align: right;
}

.price-input:disabled {
    color: #667085;
    background: #f2f4f7;
}

.unit {
    min-width: 18px;
    color: #667085;
    font-size: 13px;
}

.fee-note {
    display: inline-flex;
    align-items: center;
    min-height: 30px;
    padding: 4px 10px;
    border-radius: 999px;
    color: #475467;
    background: #f2f4f7;
    font-size: 12px;
}

.section-note {
    margin: 0;
    padding: 12px 24px;
    border-top: 1px solid #edf0f5;
    color: #667085;
    background: #fafbfc;
    font-size: 12px;
}

.section-note i {
    margin-right: 5px;
    color: #3273dc;
}

.default-setting {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 24px;
    padding: 20px 24px;
    border-top: 1px solid #e5e9f0;
    background: #f8faff;
}

.default-setting label {
    color: #344054;
    font-size: 14px;
    font-weight: 700;
}

.default-setting p {
    margin: 3px 0 0;
    color: #7a8496;
    font-size: 12px;
}

.default-setting .select,
.default-setting select {
    min-width: 220px;
}

.save-bar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 20px;
    padding: 18px 20px;
    border: 1px solid #dfe5ee;
    border-radius: 10px;
    background: #fff;
    box-shadow: 0 6px 20px rgba(31, 41, 55, 0.08);
}

.save-bar p {
    margin: 0;
    color: #667085;
    font-size: 12px;
}

.modal-card {
    width: calc(100% - 32px);
    max-width: 660px;
}

.import-description {
    margin-bottom: 20px;
    color: #667085;
    font-size: 13px;
}

.import-field-title {
    margin-bottom: 10px;
    color: #344054;
    font-size: 13px;
    font-weight: 700;
}

.import-fields {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 10px;
}

.import-option {
    display: flex;
    align-items: center;
    gap: 9px;
    min-height: 46px;
    padding: 10px 12px;
    border: 1px solid #e4e9f1;
    border-radius: 7px;
    color: #475467;
    font-size: 13px;
    cursor: pointer;
}

.import-option input {
    width: 16px;
    height: 16px;
    accent-color: #3273dc;
}

.import-field-actions {
    display: flex;
    gap: 8px;
    margin-top: 14px;
}

.import-modal-foot {
    justify-content: flex-end;
    gap: 8px;
}

.import-modal-foot .button {
    margin: 0;
}

@media screen and (max-width: 768px) {
    .page-header {
        align-items: stretch;
        flex-direction: column;
    }

    .setting-header-buttons {
        flex-wrap: wrap;
    }

    .setting-header-buttons .button {
        flex: 1;
    }

    .access-grid,
    .import-fields {
        grid-template-columns: 1fr;
    }

    .date-range {
        align-items: stretch;
        flex-direction: column;
        gap: 10px;
    }

    .date-separator {
        display: none;
    }

    .setting-table-header {
        display: none;
    }

    .setting-row {
        grid-template-columns: 1fr;
        gap: 10px;
        padding: 14px 18px;
    }

    .section-heading {
        align-items: flex-start;
        padding: 18px;
    }

    .form-grid,
    .period-body {
        padding-right: 18px;
        padding-left: 18px;
    }

    .default-setting,
    .save-bar {
        align-items: stretch;
        flex-direction: column;
    }

    .default-setting .select,
    .default-setting select,
    .save-bar .button {
        width: 100%;
    }

    .import-modal-foot {
        align-items: stretch;
        flex-direction: column-reverse;
    }
}

</style>



// WEBPACK FOOTER //
// src/components/photographer/pages/Setting.vue